/**
 * Type definitions for the standalone SSN Card verification SDK.
 * The shapes track the dedicated document verification API
 * (`/v1/verifications/documents`)
 */

// ============================================================================
// API request / response
// ============================================================================

/**
 * Inputs for creating an SSN card verification. Both card sides are captured in
 * a single Regula reader mount and uploaded together.
 */
export interface SsnCardVerificationRequest {
  sessionToken: string;
  /** Base64 (data URL or raw) cropped image of the front of the card. */
  imageFrontBase64: string;
  /** Base64 (data URL or raw) cropped image of the back of the card. */
  imageBackBase64: string;
  /** Caller-supplied correlation id. */
  externalReferenceId: string;
}

/** Result of creating a verification — the id to poll. */
export interface SsnCardCreateResponse {
  verificationId: string;
}

export type SsnCardVerificationStatus = "initiated" | "processing" | "completed" | "failed";

/** Unsupervised flow: `pending` until the server-side recognition resolves. */
export type SsnCardVerificationOutcome =
  | "pending"
  | "passed"
  | "failed"
  | "fraudulent"
  | "abandoned";

/**
 * Poll response (GET /v1/verifications/documents/{uuid}). Owns its own shape; the
 * `attributes` are the normalized credential fields returned by the provider (an
 * SSN is carried in `documentNumber`).
 */
export interface SsnCardVerificationResponse {
  uuid: string;
  type?: string;
  status: SsnCardVerificationStatus;
  outcome: SsnCardVerificationOutcome;
  reason?: { code: number; name: string; description: string } | null;
  results?: {
    provider: string;
    /** Derived resources (front/back document descriptors) issued on success. */
    resources: Array<{ uuid: string; href: string; resourceType?: string }>;
    attributes?: {
      nameFirst?: string;
      nameLast?: string;
      dateOfBirth?: string;
      documentNumber?: string;
      documentType?: string;
      issuingCountry?: string;
      issuingState?: string;
      [key: string]: unknown;
    };
    providerData?: Record<string, unknown>;
  };
}

// ============================================================================
// Error taxonomy (owned, never `DocumentErrorPayload`)
//
// Every failure is terminal — there is no client-side capture-retry loop. A bad
// capture (quality/timeout) reports the generic `ssn_card_verification_failed`
// code plus a specific `feedback` detail (mirrors iProov: one error code + a
// feedback string), so the parent keeps the *why* without a per-reason code. The
// reason -> code mapping lives in the controller hook; this file owns the vocabulary.
// ============================================================================

/** Terminal failures: stop and report to the parent. */
export const TERMINAL_SSN_CARD_ERROR_CODES = [
  "no_camera",
  "unsupported_device",
  // Camera permission denied — its own code (a device/permission issue, not a
  // failed verification). Terminal: the unsupervised flow cannot prompt a retry.
  "camera_permission_denied",
  "verification_rejected",
  "session_invalid",
  "token_expired",
  "network_error",
  "internal_error",
  "capture_init_failed",
  // User closed the reader — terminal; the parent (IVA) decides whether to relaunch
  // (mirrors legal-id's Veriff MESSAGES.CANCELED → "user_canceled").
  "user_canceled",
  // The user could not produce a usable capture (quality/timeout). Generic by design;
  // the specific reason rides along in `feedback`. Distinct from internal_error (a code fault).
  "ssn_card_verification_failed",
  // User indicated on the info screen that they don't have an SSN card — mirrors
  // legal-id's "user_no_id" (a proactive user statement, not a capture failure).
  "user_no_ssn_card",
  // User denied the extracted PII (name/SSN) was accurate on the confirmation
  // screen and asked to retake — mirrors legal-id's "user_retake_requested"
  // (VEX-7846). IVA owns the retry-limit/step-up-to-human-review decision; the
  // SDK just reports the request and ends this launch.
  "user_retake_requested",
  // Same request, but sent when the confirmation screen already showed the
  // out-of-retakes variant (`outOfRetakes: true`, VEX-7961) — i.e. IVA already
  // told this launch a retake would exhaust its verify-attempt budget. Reported
  // distinctly so IVA doesn't have to infer the member's screen state from its
  // own errorCode; the retry-limit decision itself is still IVA's.
  "retake_limit_reached",
  // Confirm was invoked with no verification result in state — should be
  // unreachable (ConfirmationScreen only renders once one exists), but named
  // distinctly from generic "internal_error" so it's identifiable in logs/Sentry
  // if the guard ever actually fires (VEX-7846).
  "confirmation_data_missing",
] as const;

export type TerminalSsnCardErrorCode = (typeof TERMINAL_SSN_CARD_ERROR_CODES)[number];
export type SsnCardErrorCode = TerminalSsnCardErrorCode;

/**
 * Controlled `feedback` tokens for the Regula capture layer — the specific reason
 * behind a terminal `errorCode` (the iProov pattern: one code + a feedback string).
 *
 * This is the SSN card flow's one deviation from legal-id's `DocumentErrorPayload`,
 * which has no `feedback` field: Veriff exposes only coarse messages, but Regula
 * emits 16 granular `ErrorTypes`, so the SSN flow keeps a feedback channel to
 * preserve that detail. Backend/transport detail still rides in `statusCode` /
 * `failedApi` / `data` exactly like legal-id — never in `feedback`.
 *
 * These are the tokens WE generate. Device/init failures additionally pass the raw
 * Regula `ErrorTypes` value through `feedback` verbatim (e.g. "CAMERA_PERMISSION_DENIED").
 */
export const SSN_CARD_CAPTURE_FEEDBACK = {
  TIMEOUT: "capture_timeout",
  QUALITY_FAILED: "capture_quality_failed",
  NO_RESULT: "no_capture_result",
  NO_READER_EVENT: "no_reader_event",
  EXTRACT_FAILED: "extract_failed",
} as const;
export type SsnCardCaptureFeedback = (typeof SSN_CARD_CAPTURE_FEEDBACK)[keyof typeof SSN_CARD_CAPTURE_FEEDBACK];

/** Which API call failed (no PATCH — the unsupervised flow has no accept step). */
export type SsnCardFailedApi = "post_ssn_card_failed" | "get_ssn_card_failed";

export interface SsnCardErrorPayload {
  errorCode: SsnCardErrorCode;
  message: string;
  /**
   * Precise reason behind a terminal `errorCode`, for the Regula capture layer only
   * (mirrors iProov's feedback). Either a controlled {@link SSN_CARD_CAPTURE_FEEDBACK}
   * token, or — for device/init failures — the raw Regula `ErrorTypes` value verbatim
   * (e.g. "CAMERA_PERMISSION_DENIED", "WASM_LICENSE"). Backend errors do NOT set this;
   * their detail rides in `statusCode` / `failedApi` / `data` (the legal-id pattern).
   */
  feedback?: string;
  /** HTTP status code (400, 500, …); absent for network errors. */
  statusCode?: number;
  failedApi?: SsnCardFailedApi;
  /** Fallback error response data. */
  data?: unknown;
}

// ============================================================================
// PostMessage payloads (success path mirrors the SDK contract; owned here)
// ============================================================================

export interface SsnCardSuccessPayload {
  /** Raw provider/backend status response for the verification. */
  data: SsnCardVerificationResponse;
}
